/**
 * CustomPagesPanel — mini-CMS inside BusinessEditPage that lets an
 * owner/manager/editor add named pages (About, Portfolio, Testimonials,
 * etc). The pages render on the public /business/:slug profile as
 * extra tabs.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { FileText, Loader2, Save, X, Trash2 } from "lucide-react";

export default function CustomPagesPanel({ biz, onChange }) {
  const [pages, setPages] = useState(biz.custom_pages || []);
  const [editing, setEditing] = useState(null); // page object or {new:true}
  const [busy, setBusy] = useState(false);

  useEffect(() => { setPages(biz.custom_pages || []); }, [biz.custom_pages]);

  const startNew = () => setEditing({ new: true, id: null, slug: "", title: "", body_html: "", order: pages.length });

  const save = async (p) => {
    setBusy(true);
    try {
      await http.post(`/businesses/${biz.id}/custom-pages`, {
        id: p.id, slug: p.slug || p.title, title: p.title, body_html: p.body_html, order: p.order || 0,
      });
      toast.success("Page saved.");
      setEditing(null);
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };

  const del = async (p) => {
    if (!confirm(`Delete the "${p.title}" page?`)) return;
    try {
      await http.delete(`/businesses/${biz.id}/custom-pages/${p.id}`);
      toast.success("Page deleted.");
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  if (editing) return (
    <div className="wofy-card !p-6 space-y-3" data-testid="custom-page-editor">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl">{editing.new ? "Add a page" : `Edit "${editing.title}"`}</h3>
        <button onClick={() => setEditing(null)} className="opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
      </div>
      <label className="block"><div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">Title</div>
        <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="wofy-input" data-testid="cpe-title" />
      </label>
      <label className="block"><div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">URL slug (auto from title)</div>
        <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="wofy-input" placeholder="about-us" data-testid="cpe-slug" />
      </label>
      <label className="block"><div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">Body (HTML allowed — h2, p, ul/li, strong, a)</div>
        <textarea rows={14} value={editing.body_html} onChange={(e) => setEditing({ ...editing, body_html: e.target.value })} className="wofy-input font-mono text-xs" data-testid="cpe-body" />
      </label>
      <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: "var(--wofy-border)" }}>
        <button onClick={() => setEditing(null)} className="wofy-btn-ghost">Cancel</button>
        <button onClick={() => save(editing)} disabled={busy || !editing.title.trim()} className="wofy-btn-primary inline-flex items-center gap-2" data-testid="cpe-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save page
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="custom-pages-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl">Custom pages</h2>
          <p className="text-xs opacity-70">These show as extra tabs on your business page. Great for About, Portfolio, Testimonials, Case studies, etc.</p>
        </div>
        <button onClick={startNew} className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="add-custom-page">
          <FileText className="w-4 h-4" /> Add page
        </button>
      </div>
      {pages.length === 0 ? (
        <div className="wofy-card !p-6 text-center opacity-70 text-sm">No custom pages yet. Add one — or use the <b>Import website</b> tab to auto-create them.</div>
      ) : (
        <ul className="divide-y" style={{ borderColor: "var(--wofy-border)" }}>
          {pages.map((p) => (
            <li key={p.id} className="py-2 flex items-center gap-3" data-testid={`custom-page-${p.slug}`}>
              <FileText className="w-4 h-4 opacity-60" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{p.title}</div>
                <div className="text-[11px] opacity-70 font-mono">/{p.slug}</div>
              </div>
              <button onClick={() => setEditing({ ...p })} className="text-xs opacity-80 hover:opacity-100 underline">Edit</button>
              <button onClick={() => del(p)} className="text-xs text-red-600 hover:opacity-80" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
